import React, { useEffect, useMemo, useState } from "react";
import { NeoButton, NeoInput } from "../../../../../components/Common";
import { toast } from "react-toastify";
import ConfirmDialog from "../../../../../components/Common/ConfirmDialog";
import { useTenant } from "../../../../../hooks/useTenant";
import WodBenchmarkLibraryService from "../../../../../services/wod_benchmark_library_service";
import { WOD_BENCHMARK_GROUPS, WOD_BENCHMARK_LIBRARY } from "../../domain/wod/benchmarkLibrary";
import {
    DEFAULT_WOD_MOVEMENT,
    getScoreTypeForWodFormat,
    WOD_COMPONENT_TYPES,
    WOD_FORMATS,
    WOD_SCORE_TYPES,
    WOD_TRACKS,
} from "../../domain/wod/constants";
import Swal from "sweetalert2";

const optionHtml = (items = [], selected = "") =>
    items.map(item => `<option value="${item.value}" ${item.value === selected ? "selected" : ""}>${item.label}</option>`).join("");

const splitCsv = (value = "") =>
    value.split(",").map(item => item.trim()).filter(Boolean);

const buildEditableBenchmark = (benchmark = {}, formValues = {}) => {
    const format = formValues.format || benchmark.format || "amrap";
    const scoreType = formValues.scoreType || getScoreTypeForWodFormat(format);
    const existingBlock = benchmark.blocks?.[0] || {};
    const sets = existingBlock.sets?.length ? existingBlock.sets : [{ ...DEFAULT_WOD_MOVEMENT }];

    return {
        ...benchmark,
        name: formValues.name || benchmark.name || "WOD da Box",
        group: formValues.group || benchmark.group || "box",
        track: formValues.track || benchmark.track || "crossfit",
        format,
        scoreType,
        timeCap: formValues.timeCap ?? benchmark.timeCap ?? "",
        rounds: formValues.rounds ?? benchmark.rounds ?? "",
        stimulus: formValues.stimulus ?? benchmark.stimulus ?? "",
        equipment: formValues.equipment || benchmark.equipment || [],
        tags: formValues.tags || benchmark.tags || ["box"],
        blocks: [{
            ...existingBlock,
            name: formValues.name || existingBlock.name || benchmark.name || "WOD da Box",
            workoutType: "wod",
            componentType: formValues.componentType || existingBlock.componentType || "metcon",
            format,
            scoreType,
            timeCap: formValues.timeCap ?? existingBlock.timeCap ?? benchmark.timeCap ?? "",
            rounds: formValues.rounds ?? existingBlock.rounds ?? benchmark.rounds ?? "",
            stimulus: formValues.stimulus ?? existingBlock.stimulus ?? benchmark.stimulus ?? "",
            scalingNotes: formValues.scalingNotes ?? existingBlock.scalingNotes ?? "",
            sets,
        }],
    };
};

const showBenchmarkForm = async (benchmark = {}) => Swal.fire({
    title: benchmark.id ? "Editar WOD da Box" : "Novo WOD da Box",
    width: 760,
    html: `
        <div class="row g-3 text-start">
            <div class="col-md-7">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Nome do WOD</label>
                <input id="wod-name" class="form-control" placeholder="Ex: Sexta Engine" value="${benchmark.name || ""}">
            </div>
            <div class="col-md-5">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Grupo</label>
                <select id="wod-group" class="form-control">${optionHtml(WOD_BENCHMARK_GROUPS, benchmark.group || "box")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Track</label>
                <select id="wod-track" class="form-control">${optionHtml(WOD_TRACKS, benchmark.track || "crossfit")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Componente</label>
                <select id="wod-component" class="form-control">${optionHtml(WOD_COMPONENT_TYPES, benchmark.blocks?.[0]?.componentType || "metcon")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Formato</label>
                <select id="wod-format" class="form-control">${optionHtml(WOD_FORMATS, benchmark.format || "amrap")}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Score</label>
                <select id="wod-score" class="form-control">${optionHtml(WOD_SCORE_TYPES, benchmark.scoreType || getScoreTypeForWodFormat(benchmark.format || "amrap"))}</select>
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Time cap</label>
                <input id="wod-time-cap" class="form-control" placeholder="20 min" value="${benchmark.timeCap || ""}">
            </div>
            <div class="col-md-4">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Rounds</label>
                <input id="wod-rounds" class="form-control" placeholder="3" value="${benchmark.rounds || ""}">
            </div>
            <div class="col-md-6">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Equipamentos</label>
                <input id="wod-equipment" class="form-control" placeholder="Barra, Remo, Kettlebell" value="${(benchmark.equipment || []).join(", ")}">
            </div>
            <div class="col-md-6">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Tags</label>
                <input id="wod-tags" class="form-control" placeholder="engine, benchmark, open" value="${(benchmark.tags || ["box"]).join(", ")}">
            </div>
            <div class="col-12">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Estímulo</label>
                <textarea id="wod-stimulus" class="form-control" rows="2" placeholder="Intenção do treino, ritmo e foco técnico.">${benchmark.stimulus || ""}</textarea>
            </div>
            <div class="col-12">
                <label class="form-label font-size-12 fw-bold text-muted mb-1">Scaling geral</label>
                <textarea id="wod-scaling" class="form-control" rows="2" placeholder="Adaptações gerais por nível.">${benchmark.blocks?.[0]?.scalingNotes || ""}</textarea>
            </div>
        </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Salvar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f59e0b",
    cancelButtonColor: "#6c757d",
    preConfirm: () => {
        const name = document.getElementById("wod-name").value.trim();
        const format = document.getElementById("wod-format").value;

        if (!name) {
            Swal.showValidationMessage("O nome do WOD é obrigatório.");
            return false;
        }

        return {
            name,
            group: document.getElementById("wod-group").value,
            track: document.getElementById("wod-track").value,
            componentType: document.getElementById("wod-component").value,
            format,
            scoreType: document.getElementById("wod-score").value || getScoreTypeForWodFormat(format),
            timeCap: document.getElementById("wod-time-cap").value.trim(),
            rounds: document.getElementById("wod-rounds").value.trim(),
            equipment: splitCsv(document.getElementById("wod-equipment").value),
            tags: splitCsv(document.getElementById("wod-tags").value),
            stimulus: document.getElementById("wod-stimulus").value.trim(),
            scalingNotes: document.getElementById("wod-scaling").value.trim(),
        };
    },
});

const WodBenchmarkLibraryPicker = ({ refreshKey = 0, onAddBenchmark }) => {
    const { idTenant, user: authUser } = useTenant();
    const [customBenchmarks, setCustomBenchmarks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [groupFilter, setGroupFilter] = useState("all");
    const [searchQuery, setSearchQuery] = useState("");

    const loadCustomBenchmarks = React.useCallback(async (mounted = true) => {
        if (!idTenant) return;
        setLoading(true);
        try {
            const data = await WodBenchmarkLibraryService.getAll(idTenant);
            if (mounted) setCustomBenchmarks(data);
        } catch (error) {
            console.error("Erro ao carregar biblioteca de WODs:", error);
        } finally {
            if (mounted) setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        let mounted = true;
        loadCustomBenchmarks(mounted);
        return () => {
            mounted = false;
        };
    }, [loadCustomBenchmarks, refreshKey]);

    const items = useMemo(() => [
        ...customBenchmarks.map(item => ({ ...item.benchmark, id: item.id, source: "tenant" })),
        ...WOD_BENCHMARK_LIBRARY.map(item => ({ ...item, source: "system" })),
    ], [customBenchmarks]);

    const visibleItems = items.filter(item => {
        const query = searchQuery.trim().toLowerCase();
        const matchesGroup = groupFilter === "all" || item.group === groupFilter;
        const matchesSearch = !query ||
            item.name.toLowerCase().includes(query) ||
            (item.tags || []).some(tag => tag.toLowerCase().includes(query)) ||
            (item.equipment || []).some(equipment => equipment.toLowerCase().includes(query));
        return matchesGroup && matchesSearch;
    });

    const handleDelete = async (event, item) => {
        event.stopPropagation();
        const confirmed = await ConfirmDialog({
            title: "Excluir WOD?",
            text: `Deseja remover "${item.name}" da biblioteca da box?`,
            confirmButtonText: "Sim, excluir",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#dc3545",
        });

        if (!confirmed) return;

        try {
            await WodBenchmarkLibraryService.delete({ idTenant, benchmarkId: item.id });
            await loadCustomBenchmarks(true);
            toast.success("WOD removido da biblioteca.");
        } catch (error) {
            toast.error(error.message || "Erro ao remover WOD.");
        }
    };

    const handleCreate = async () => {
        const { value: formValues } = await showBenchmarkForm();
        if (!formValues) return;

        try {
            await WodBenchmarkLibraryService.save({
                idTenant,
                authUser,
                benchmark: buildEditableBenchmark({}, formValues),
            });
            await loadCustomBenchmarks(true);
            toast.success("WOD criado na biblioteca da box.");
        } catch (error) {
            toast.error(error.message || "Erro ao criar WOD.");
        }
    };

    const handleEdit = async (event, item) => {
        event.stopPropagation();
        const { value: formValues } = await showBenchmarkForm(item);
        if (!formValues) return;

        try {
            await WodBenchmarkLibraryService.update({
                idTenant,
                benchmarkId: item.id,
                benchmark: buildEditableBenchmark(item, formValues),
            });
            await loadCustomBenchmarks(true);
            toast.success("WOD atualizado.");
        } catch (error) {
            toast.error(error.message || "Erro ao atualizar WOD.");
        }
    };

    return (
        <div className="wod-benchmark-library">

            <div className="mb-2">
                <NeoInput
                    type="text"
                    placeholder="Buscar WOD, equipamento ou tag..."
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    className="font-size-13 mb-2"
                    size="sm"
                />
                <div className="d-flex align-items-center justify-content-between gap-2">
                    <div>
                        <h6 className="mb-0 fw-bold font-size-13 text-dark">Biblioteca de WODs</h6>
                        <span className="text-muted font-size-11">Benchmarks do sistema e treinos da box</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                        <NeoButton
                            type="button"
                            size="sm"
                            variant="soft-secondary"
                            className="d-flex align-items-center gap-1 fw-semibold border-0"
                            onClick={handleCreate}
                        >
                            <i className="mdi mdi-plus" />
                            Novo
                        </NeoButton>
                        <NeoInput
                            type="select"
                            size="sm"
                            value={groupFilter}
                            onChange={(event) => setGroupFilter(event.target.value)}
                            className="font-size-12"
                        >
                            <option value="all">Todos</option>
                            {WOD_BENCHMARK_GROUPS.map(group => (
                                <option key={group.value} value={group.value}>{group.label}</option>
                            ))}
                        </NeoInput>
                        {loading && <i className="mdi mdi-loading mdi-spin text-primary ms-2" />}
                    </div>
                </div>
            </div>

            <div className="swim-set-library-grid">
                {visibleItems.map(item => {
                    const group = WOD_BENCHMARK_GROUPS.find(groupItem => groupItem.value === item.group)?.label || item.group;
                    return (
                        <button
                            key={`${item.source}-${item.id}`}
                            type="button"
                            className="wod-benchmark-item"
                            onClick={() => onAddBenchmark(item)}
                        >
                            <span className="fw-bold text-dark font-size-13">{item.name}</span>
                            <span className="text-muted font-size-11">
                                {group} • {item.format?.toUpperCase?.() || item.format} • {item.timeCap || "sem time cap"} • {item.source === "tenant" ? "Box" : "Sistema"}
                            </span>
                            {item.stimulus && (
                                <span className="text-muted font-size-11">{item.stimulus}</span>
                            )}
                            {item.source === "tenant" && (
                                <span className="wod-benchmark-actions">
                                    <button type="button" className="btn btn-sm btn-light border-0 text-secondary" title="Editar WOD" onClick={(event) => handleEdit(event, item)}>
                                        <i className="mdi mdi-pencil-outline" />
                                    </button>
                                    <button type="button" className="btn btn-sm btn-light border-0 text-danger" title="Excluir WOD" onClick={(event) => handleDelete(event, item)}>
                                        <i className="mdi mdi-trash-can-outline" />
                                    </button>
                                </span>
                            )}
                        </button>
                    );
                })}
                {visibleItems.length === 0 && (
                    <div className="text-center py-4 bg-light rounded border border-dashed text-muted font-size-12">
                        Nenhum WOD encontrado.
                    </div>
                )}
            </div>
        </div>
    );
};

export default WodBenchmarkLibraryPicker;
